import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Loader2, Scissors, Sparkles, X } from "lucide-react";
import { api, type DeckGoalKey, type RegenerateJobView, type ThinDeckRowView } from "../../lib/api.js";
import { CONTENT_SLIDE_CHOICES } from "../../components/social/deckGoals.js";
import { useDismiss } from "../../components/ui/useDismiss.js";

/**
 * Every deck post that says too little, before it goes out.
 *
 * The operator finds a thin post by seeing it in a feed, which is after the
 * fact (Helicobacter story, 30.9). This reads every carousel, TikTok photo post
 * and story in the pool — scheduled or not — through the same profile the
 * calendar dialog shows, and offers the cheapest fix first: a story whose pack
 * already has a longer carousel is re-cut from it for free; anything else is
 * expanded by a rewrite with an explicit goal and slide count.
 */

const PLATFORM_HE: Record<string, string> = { facebook: "פייסבוק", instagram: "אינסטגרם", tiktok: "טיקטוק", youtube: "יוטיוב" };
const FORMAT_HE: Record<string, string> = { carousel: "קרוסלה", photo_post: "פוסט תמונות", story: "סטורי" };

function il(iso: string | null): string {
  if (!iso) return "לא בלוח";
  return new Intl.DateTimeFormat("he-IL", { timeZone: "Asia/Jerusalem", weekday: "short", day: "numeric", month: "numeric", hour: "2-digit", minute: "2-digit" }).format(new Date(iso));
}

export function ThinDecksDialog({ onClose, onChanged }: { onClose: () => void; onChanged: () => void }) {
  const dismiss = useDismiss(onClose);
  const q = useQuery({ queryKey: ["thin-decks"], queryFn: () => api.thinDecks() });
  const [note, setNote] = useState<string | null>(null);

  const localStories = (q.data?.rows ?? []).filter((r) => r.format === "story" && !r.inPostiz && r.recutContent !== null);
  const recutAll = useMutation({
    mutationFn: () => api.recutLocalStories(),
    onSuccess: (r) => { setNote(`נחתכו מחדש ${r.recut.length} סטוריז.`); void q.refetch(); onChanged(); },
    onError: (e) => setNote((e as Error).message),
  });

  return (
    <div {...dismiss} className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" dir="rtl">
      <div className="flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl bg-white shadow-xl">
        <header className="flex shrink-0 items-center justify-between border-b border-line px-4 py-3">
          <h2 className="text-sm font-semibold">פוסטים רזים{q.data ? ` (${q.data.rows.length})` : ""}</h2>
          <button onClick={onClose} className="text-muted hover:text-ink"><X size={16} /></button>
        </header>

        <div className="min-h-0 grow space-y-3 overflow-y-auto p-4 text-sm">
          <p className="text-[11px] text-muted">
            קרוסלות, פוסטי תמונות וסטוריז שעוד לא פורסמו, עם פחות מ־{q.data?.minContent ?? 4} שקופיות תוכן
            (בלי פתיחה וסיום), או פחות ממה שהכותרת או הכיתוב מבטיחים.
          </p>

          {localStories.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 rounded-lg bg-emerald-50 px-3 py-2 text-xs text-emerald-900">
              <span>{localStories.length} סטוריז שעוד לא בפוסטיז אפשר לחתוך מחדש מהקרוסלה של אותו מאמר, בלי AI ובלי עלות.</span>
              <button
                onClick={() => recutAll.mutate()}
                disabled={recutAll.isPending}
                className="flex items-center gap-1.5 rounded-lg bg-emerald-700 px-2.5 py-1 font-medium text-white disabled:opacity-40"
              >
                {recutAll.isPending ? <Loader2 size={13} className="animate-spin" /> : <Scissors size={13} />}
                לחתוך את כולם
              </button>
            </div>
          )}
          {note && <p className="text-xs text-emerald-800">{note}</p>}

          {q.isLoading && <p className="flex items-center gap-2 text-xs text-muted"><Loader2 size={13} className="animate-spin" /> טוען…</p>}
          {q.error && <p className="text-xs text-danger">{(q.error as Error).message}</p>}
          {q.data && q.data.rows.length === 0 && <p className="text-xs text-muted">אין פוסטים רזים.</p>}

          <ul className="space-y-2">
            {q.data?.rows.map((r) => (
              <ThinRow key={r.id} row={r} goals={q.data!.goals} onDone={() => { void q.refetch(); onChanged(); }} />
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function ThinRow({ row, goals, onDone }: {
  row: ThinDeckRowView;
  goals: { goal: DeckGoalKey; labelHe: string; whenHe: string }[];
  onDone: () => void;
}) {
  const [goal, setGoal] = useState<DeckGoalKey | "">("");
  const [count, setCount] = useState<number>(row.profile.suggestedContent);
  const [job, setJob] = useState<RegenerateJobView | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const running = job?.status === "running";

  // The rewrite runs in the background (two attempts can pass the gateway's
  // 60s); poll it here like the calendar dialog does.
  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => {
      void api.regenerateStatus(row.id).then((r) => {
        if (!r.job) return;
        setJob(r.job);
        if (r.job.status !== "running") {
          const ok = r.job.status === "done" && r.job.outcome === 200;
          setMsg(ok ? `הורחב · $${r.job.costUsd.toFixed(3)}` : (r.job.error ?? "ההרחבה נכשלה"));
          if (ok) onDone();
        }
      }).catch(() => {});
    }, 2500);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running, row.id]);

  const recut = useMutation({
    mutationFn: () => api.recutStory(row.id),
    onSuccess: (r) => {
      setMsg(`נחתך מחדש: ${r.before} ← ${r.after}` + (r.postiz ? (r.postiz.ok ? " · עודכן בפוסטיז" : ` · פוסטיז: ${r.postiz.error}`) : ""));
      onDone();
    },
    onError: (e) => setMsg((e as Error).message),
  });
  const expand = useMutation({
    mutationFn: () => api.regenerateSocialVariant(row.id, undefined, { ...(goal ? { goal } : {}), contentSlides: count }),
    onSuccess: (r) => { setMsg(null); setJob(r.job); },
    onError: (e) => setMsg((e as Error).message),
  });

  return (
    <li className="rounded-lg border border-line p-2.5">
      <div className="flex flex-wrap items-baseline gap-x-2 text-xs">
        <span className="font-medium">{row.label ?? row.hook}</span>
        <span className="text-muted">
          {PLATFORM_HE[row.platform]} · {FORMAT_HE[row.format] ?? row.format} · {il(row.scheduledFor)}
          {row.inPostiz ? " · בפוסטיז" : ""}
        </span>
      </div>
      <p className="mt-0.5 text-[11px] text-muted">
        {row.profile.total} שקופיות, {row.profile.content} עם תוכן · מטרה: {row.profile.goalLabelHe}
      </p>
      <ul className="mt-1 list-disc pr-4 text-[11px] text-amber-900">
        {row.profile.reasonsHe.map((x, i) => <li key={i}>{x}</li>)}
      </ul>

      <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px]">
        {row.recutContent !== null && (
          <button
            onClick={() => recut.mutate()}
            disabled={recut.isPending || running}
            title={row.inPostiz ? "הסטורי כבר בפוסטיז: הוא יימשך וייווצר שם מחדש." : "מקומי בלבד."}
            className="flex items-center gap-1 rounded-lg border border-emerald-700 px-2 py-1 font-medium text-emerald-800 disabled:opacity-40"
          >
            {recut.isPending ? <Loader2 size={12} className="animate-spin" /> : <Scissors size={12} />}
            חיתוך מהקרוסלה · {row.recutContent} עם תוכן · חינם
          </button>
        )}
        <select value={goal} onChange={(e) => setGoal(e.target.value as DeckGoalKey | "")} className="rounded border border-line bg-canvas px-1.5 py-1">
          <option value="">מטרה: לפי המאמר</option>
          {goals.map((g) => <option key={g.goal} value={g.goal}>{g.labelHe}</option>)}
        </select>
        <select value={count} onChange={(e) => setCount(Number(e.target.value))} className="rounded border border-line bg-canvas px-1.5 py-1">
          {CONTENT_SLIDE_CHOICES.map((n) => <option key={n} value={n}>{n} שקופיות תוכן</option>)}
        </select>
        <button
          onClick={() => expand.mutate()}
          disabled={expand.isPending || running || recut.isPending}
          className="flex items-center gap-1 rounded-lg border border-brand-700 px-2 py-1 font-medium text-brand-800 disabled:opacity-40"
        >
          {expand.isPending || running ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
          {running ? "מרחיב…" : "הרחבה עם AI · ~$0.05"}
        </button>
      </div>
      {msg && <p className="mt-1 text-[11px] text-muted">{msg}</p>}
    </li>
  );
}
